import * as Sentry from '@sentry/react';
import {CancelledError} from '@tanstack/query-core';
import {AnyAction, Dispatch} from 'redux';
import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {tolgeeInstance} from 'tolgee';

import * as API from '~api/images';
import {ImageQueryKeys} from '~api/images.queries';
import {getModelsV2} from '~api/models';
import {ModelsQueryKeys} from '~api/models.queries';
import {splitImageOrderBy} from '~utils/miscUtils';
import {IMAGES_FETCH_ERROR} from '~redux/types';
import {Model} from '~redux/types/models';
import {Session} from '~redux/types/session';
import {annotatorActions} from '~redux/reducers/annotatorReducer';
import {resetError, setError} from '~redux/actions/errorActions';
import {AppDispatch, ReduxState} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {CLASS_OK} from 'src/constants/constants';
import {e2eFetchQueryOptions, queryClient} from 'src/queryClient';
import {shuffle} from 'src/utils/shuffle';
import {selectMetadata, selectProductTypes} from '../reducers/filtersReducer';
import {getUtilStateIsLoading} from '../reducers/globalReducer';
import {
  imageDataActions,
  selectCurrentImage,
  selectHasImageFetchError,
  selectImagesActualTotalCount,
  selectImagesData,
  selectImagesTotalCount,
  selectItemsPerPage,
} from '../reducers/imageReducer';
import {selectCurrentProjectId, selectUserEmail, selectUserId} from '../reducers/userReducer';
import {
  AnnotationTelemetryPayload,
  ImageData,
  ImageFilters,
  ImageLabelPayloadByModel,
  ImageMeta,
  ImageMetadata,
  ImageOrderBy,
  ImagesData,
  LabelItem,
  PaginationActionWithTargetPage,
  UpdateImagesDataPayload,
  UpdateImagesDataPayloadItem,
} from '../types/images';
import {fetchFilters} from './filtersActions';
import {
  decreaseGlobalLoadingIndicator,
  increaseBlockingLoadingIndicator,
  increaseGlobalLoadingIndicator,
} from './globalActions';
import {fetchUserData} from './userActions';

const isE2eBuild = import.meta.env.VITE_E2E === 'true';

const fetchFiltersIfMissing =
  (): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    if (isE2eBuild && (selectProductTypes(getState()) ?? []).length > 0) {
      return;
    }
    await dispatch(fetchFilters());
  };

const fetchUserDataIfMissing =
  (): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    if (isE2eBuild && selectUserId(getState()) !== '') {
      return;
    }
    await dispatch(fetchUserData());
  };

export const setImageIndexWhenNotLoading =
  (imageIndex: number) =>
  (dispatch: Dispatch, getState: () => ReduxState): void => {
    if (getUtilStateIsLoading(getState())) {
      return;
    }

    dispatch(imageDataActions.setImageIndex(imageIndex));
  };

/**
 * Use react-query to fetch images data based on all provided arguments. See explanation in {@link fetchFilteredImagesData}.
 */
export async function fetchImagesData({
  projectId,
  payload,
  targetPage,
  bustCache = false,
  metadata,
}: {
  projectId: string;
  payload: API.ImagesDataPayload;
  targetPage?: number;
  bustCache?: boolean;
  metadata?: MetadataFilters;
}): Promise<ImagesData> {
  // Results of cursor based pagination can be cached if
  // a) a cursor exists AND
  // b) images are sorted by time
  const {sortBy, sortOrder, count, action} = payload.pagination ?? {};
  const hasPaginationCursor = Boolean(action?.cursor);
  const isSortedByTime = Boolean(!sortBy || sortBy.includes('TIMESTAMP'));
  const canBeCached = !bustCache && hasPaginationCursor && isSortedByTime;

  // Set the default cache key to page 1. By doing so the response data from the initial request on the image overview page
  // will be written to the cache of page 1. This is done to enable caching when navigating back from second to first page.
  const pageCacheKey = targetPage ?? 1;
  const queryKey = ImageQueryKeys.imagesDataFilterPaginated(
    projectId,
    payload.filters,
    {sortBy, sortOrder, count},
    pageCacheKey,
  );

  if (bustCache && targetPage) {
    // When busting the cache, invalidate all pages that are greater or equal to the current page.
    const currentQueryKeyWithoutPage = queryKey.slice(0, queryKey.length - 1);
    const currentQueryKeyPage = queryKey.at(-1)!;

    await queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKeyToCheck = query.queryKey as string[];
        const queryKeyWithoutPage = queryKeyToCheck.slice(0, queryKeyToCheck.length - 1);
        const queryKeyPage = queryKeyToCheck.at(-1)!;
        return (
          JSON.stringify(queryKeyWithoutPage) === JSON.stringify(currentQueryKeyWithoutPage) &&
          queryKeyPage >= currentQueryKeyPage
        );
      },
    });
  } else if (!hasPaginationCursor) {
    // In case images are requested without a given reference cursor, clear all cache data that matches the requested filters.
    // This is done to ensure that data is always fresh when navigating away and back to the archive or defect-book.
    // If a cursor is specified, the cache is still valid as we have a save reference to use for pagination.
    await queryClient.invalidateQueries({queryKey: ImageQueryKeys.imagesDataFilter(projectId, payload.filters)});
  }

  return await queryClient.fetchQuery({
    queryKey,
    queryFn: ({signal}) => API.getImagesData(projectId, payload, signal, metadata),
    staleTime: canBeCached ? 1000 * 60 * 10 : 0,
  });
}

/**
 * This fetches all data and sets up the Redux store to show a single image (fetched by object ID) in the image annotator
 */
export const fetchImagesDataByImageID =
  (objectId: string, similaritySearch?: SimSearchFilter): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    const requestId = ++latestImagesRequestId;

    try {
      const state = getState();
      const projectId = selectCurrentProjectId(state);
      const metadata = selectMetadata(state);

      const [newImagesData] = await Promise.all([
        fetchImagesData({
          projectId,
          payload: {filters: {objectId, ...(similaritySearch && {similaritySearch})}, pagination: {count: 1}},
          metadata,
        }),
        dispatch(fetchFiltersIfMissing()), // Required for product types
        dispatch(fetchUserDataIfMissing()), // Required for tags and training tags (dataset versions))
      ]);
      await queryClient.fetchQuery({
        queryKey: ModelsQueryKeys.allModels(projectId),
        queryFn: ({signal}) => getModelsV2(projectId, signal),
        ...e2eFetchQueryOptions,
      }); // Required for models & label items

      if (requestId === latestImagesRequestId) {
        dispatch(imageDataActions.setImages(newImagesData));
        dispatch(imageDataActions.setImageIndex(0));
      }
    } catch (error: any) {
      if (requestId === latestImagesRequestId) {
        dispatch(setError(IMAGES_FETCH_ERROR, error));
      }
    }
  };

/**
 * CM-gallery counterpart of `fetchImagesDataByImageID`: the confusion cell narrows the fetched image to the
 * prediction of the cell the clicked card belongs to. Kept separate so the shared archive/defect-book path stays
 * untouched.
 */
export const fetchCmImageDataByImageID =
  (objectId: string, confusionCell?: ConfusionCell): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    const requestId = ++latestImagesRequestId;

    try {
      const state = getState();
      const projectId = selectCurrentProjectId(state);
      const metadata = selectMetadata(state);

      const [newImagesData] = await Promise.all([
        fetchImagesData({
          projectId,
          payload: {filters: {objectId, ...(confusionCell && {confusionCell})}, pagination: {count: 1}},
          metadata,
        }),
        dispatch(fetchFiltersIfMissing()),
        dispatch(fetchUserDataIfMissing()),
      ]);
      await queryClient.fetchQuery({
        queryKey: ModelsQueryKeys.allModels(projectId),
        queryFn: ({signal}) => getModelsV2(projectId, signal),
        ...e2eFetchQueryOptions,
      });

      if (requestId === latestImagesRequestId) {
        dispatch(imageDataActions.setImages(newImagesData));
        dispatch(imageDataActions.setImageIndex(0));
      }
    } catch (error: any) {
      if (requestId === latestImagesRequestId) {
        dispatch(setError(IMAGES_FETCH_ERROR, error));
      }
    }
  };

async function fetchFilteredImagesData({
  state,
  imageFilters,
  count,
  session,
  paginationActionWithTargetPage,
  bustCache,
}: FetchImagesArgs & {state: ReduxState; imageFilters: ImageFilters}): Promise<ImagesData> {
  const userEmail = selectUserEmail(state);
  const projectId = selectCurrentProjectId(state);
  const metadata = selectMetadata(state);
  const {orderBy: orderByArg, confusionMatrixCellID: _confusionMatrixCellID, ...filters} = imageFilters;

  // TODO: provide real fix once we get state management right
  // Filter out POLYGON_SIZE ordering for image view - it's only valid for instance view.
  // This prevents broken requests when switching from instance view to image view.
  const isPolygonSizeOrder =
    orderByArg === ImageOrderBy.POLYGON_SIZE_DESC || orderByArg === ImageOrderBy.POLYGON_SIZE_ASC;
  const sanitizedOrderBy = isPolygonSizeOrder ? ImageOrderBy.ANNOTATION_TIMESTAMP_DESC : orderByArg;

  const orderBy = window.location.href.includes('model/samples')
    ? (sanitizedOrderBy ?? ImageOrderBy.ANNOTATION_TIMESTAMP_DESC)
    : sanitizedOrderBy;

  /**
   * NOTE:
   *
   * Use react-query for fetching the images data so that the response will be cached based on the query key.
   * Generally, Redux caches the returned images data already, but the cache is shared between archive and defect-book
   * and we don't keep track of the query args in Redux. Therefor we never know if the images data stored in Redux belongs
   * to archive or defect-book.
   *
   * By using react-query as an additional caching layer THAT KNOWS if the data belongs to archive or defect-book by
   * the arguments being used to query the API, we don't need to refactor the Redux state. Also it provides a foundation
   * for fully switching to react-query for API data caching.
   */
  const result = await fetchImagesData({
    projectId,
    bustCache,
    payload: {
      filters: {
        ...filters,
        includeAnnotationHistory: session?.type === 'image' ? true : undefined,
        sessionTag: session?.tag,
      },
      pagination: {
        ...splitImageOrderBy(orderBy),
        action: paginationActionWithTargetPage?.action,
        count,
      },
    },
    targetPage: paginationActionWithTargetPage?.targetPage,
    metadata,
  });

  if (session && userEmail) {
    // we have a session, so we need to shuffle the data based on the users email
    result.data = shuffle(result.data, userEmail);
  }

  return result;
}

// Only the latest images-list request may write its response to redux. Without this, a slow unfiltered request can
// resolve after a fast filtered one and overwrite it (CE-2547).
// Recurring pollers (LiveTeachSession dispatches updateImagesData every second) are not starved by this guard because
// their identical arguments produce the identical react-query key, so in-flight ticks share one fetch promise and the
// latest request id always resumes with the shared response. Pollers must keep their query key constant per tick —
// a time-varying key (e.g. a timestamp filter or bustCache) would supersede every response before it resolves.
let latestImagesRequestId = 0;

export interface FetchImagesArgs {
  withGlobalLoading?: boolean;
  withDiffCheck?: boolean;
  imageFilters?: ImageFilters;
  count?: number;
  session?: Session;
  paginationActionWithTargetPage?: PaginationActionWithTargetPage;
  bustCache?: boolean;
}

export const updateImagesData =
  ({
    withGlobalLoading = false,
    withDiffCheck = false,
    imageFilters = {},
    count,
    paginationActionWithTargetPage = undefined,
    session = undefined,
    bustCache,
  }: FetchImagesArgs = {}): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    const requestId = ++latestImagesRequestId;
    const isLatestRequest = () => requestId === latestImagesRequestId;

    try {
      if (withGlobalLoading) {
        dispatch(increaseGlobalLoadingIndicator());
        dispatch(imageDataActions.increaseLoadingCounter());
      }

      const state = getState();
      const itemsPerPage = count || selectItemsPerPage(state);

      const newImagesData = await fetchFilteredImagesData({
        state,
        imageFilters,
        count: itemsPerPage,
        paginationActionWithTargetPage,
        session,
        bustCache,
      });

      if (!isLatestRequest()) {
        return;
      }

      // A previous failed fetch leaves IMAGES_FETCH_ERROR set, which replaces the whole gallery with an error alert.
      // Clear it once a fetch succeeds so the gallery recovers without a page reload.
      if (selectHasImageFetchError(getState())) {
        dispatch(resetError(IMAGES_FETCH_ERROR));
      }

      // Total is returned only if no cursor is provided to the backend, i.e. on initial load. Thus, use the previous
      // total response if no total is contained in the response.
      if (newImagesData.total === undefined) {
        newImagesData.total = selectImagesTotalCount(state);
        if (newImagesData.actualTotalCount === undefined) {
          newImagesData.actualTotalCount = selectImagesActualTotalCount(state);
        }
      }

      if (!withDiffCheck || (withDiffCheck && !isEqual(selectImagesData(state), newImagesData))) {
        dispatch(imageDataActions.setImages(newImagesData));
      }
    } catch (error: any) {
      if (isLatestRequest()) {
        dispatch(setError(IMAGES_FETCH_ERROR, error));
      }
    } finally {
      if (withGlobalLoading) {
        dispatch(decreaseGlobalLoadingIndicator());
        dispatch(imageDataActions.decreaseLoadingCounter());
      }
    }
  };

function isEqual(oldImagesData: ImagesData | null, newImagesData: ImagesData) {
  const oldData = (oldImagesData?.data || []).map(removeImageURLQueryParams);
  const newData = (newImagesData.data || []).map(removeImageURLQueryParams);
  return JSON.stringify(oldData) === JSON.stringify(newData);
}

function removeImageURLQueryParams(imageData: ImageData): ImageData {
  const dataData = imageData?.data || '';
  const dataThumbData = imageData.thumbData || '';

  const data = dataData.split('?')[0];
  const thumbData = dataThumbData.split('?')[0];

  return {...imageData, data, thumbData};
}

const fetchAllImagesData =
  (fetchArgs: FetchImagesArgs = {}): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    const projectId = selectCurrentProjectId(getState());

    try {
      // Fetch new data
      await Promise.all([
        dispatch(fetchFiltersIfMissing()), // gets product types
        queryClient.fetchQuery({
          queryKey: ModelsQueryKeys.allModels(projectId),
          queryFn: ({signal}) => getModelsV2(projectId, signal),
          ...e2eFetchQueryOptions,
        }), // Required for models & label items
        dispatch(fetchUserDataIfMissing()), // Tags and training tags (dataset versions) are retrieved from here
      ]);
      await dispatch(updateImagesData(fetchArgs));
    } catch (error: any) {
      // in case the error is a CancelledError proceed silently. This happens e.g. when
      // the react query fetch is cancelled because of deduping of requests.
      if (error instanceof CancelledError) {
        await dispatch(updateImagesData(fetchArgs));
        return;
      }
      dispatch(setError(IMAGES_FETCH_ERROR, error));
    } finally {
      dispatch(annotatorActions.setImageReadOnly(false));
    }
  };

export const resetAndFetchAllImagesData =
  (fetchArgs: FetchImagesArgs = {}): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, _getState: () => ReduxState): Promise<void> => {
    dispatch(imageDataActions.resetImages());
    await dispatch(fetchAllImagesData(fetchArgs));
  };

export const classifyImages = ({
  images,
  classLabelItem,
  modelId,
  uiContext,
}: {
  images: ImageData[];
  classLabelItem: LabelItem;
  modelId: string;
  uiContext: string;
}) => {
  return async (dispatch: AppDispatch, getState: () => ReduxState): Promise<void> => {
    try {
      const payload: UpdateImagesDataPayload = images.map((imageData): UpdateImagesDataPayloadItem => {
        return {
          imageId: imageData.id,
          labelsByModel: {
            [modelId]: [{id: classLabelItem.id, type: classLabelItem.type}],
          },
          uiContext,
        };
      });

      dispatch(increaseGlobalLoadingIndicator());

      const projectID = selectCurrentProjectId(getState());
      const updatedLabels = await API.putImagesData(projectID, payload);
      dispatch(imageDataActions.updateImageAnnotations(updatedLabels));
      showNotification({
        severity: 'success',
        message: tolgeeInstance.t('classAssignedSuccessfully', {
          name: classLabelItem.alias ?? classLabelItem.id,
          count: payload.length,
          ns: 'review',
        }),
      });
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};

export const bulkClassifyImages = ({
  filters,
  labelsByModel,
  uiContext,
  telemetry,
}: {
  filters: ImageFilters;
  labelsByModel: ImageLabelPayloadByModel;
  uiContext: string;
  telemetry?: AnnotationTelemetryPayload;
}) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      dispatch(increaseBlockingLoadingIndicator());

      const state = getState();
      const projectID = selectCurrentProjectId(state);
      const metadata = selectMetadata(state);
      await API.bulkAssignClass(projectID, {filters, labelsByModel, uiContext, telemetry}, metadata);
      dispatch(imageDataActions.resetImages());
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};

/**
 * Assign the given class labels to a single image and save them right away. When the image is the one
 * open in the annotator, the save status is updated and the change is recorded on the undo stack.
 */
const classifySingleImage = ({
  image,
  labelsByModel,
  labelName,
  uiContext,
  telemetry,
}: {
  image: ImageData;
  labelsByModel: ImageLabelPayloadByModel | undefined;
  labelName: string;
  uiContext: string;
  telemetry?: AnnotationTelemetryPayload;
}) => {
  return async (dispatch: AppDispatch, getState: () => ReduxState): Promise<void> => {
    const isCurrentImage = () => selectCurrentImage(getState())?.id === image.id;
    try {
      const payload: UpdateImagesDataPayload = [
        {
          imageId: image.id,
          labelsByModel,
          uiContext,
          telemetry,
        },
      ];

      dispatch(increaseGlobalLoadingIndicator());
      if (isCurrentImage()) {
        dispatch(annotatorActions.setIsSaving(true));
      }

      const projectID = selectCurrentProjectId(getState());
      const updatedLabels = await API.putImagesData(projectID, payload);
      dispatch(imageDataActions.updateImageAnnotations(updatedLabels));
      const changedModelIds = Object.keys(labelsByModel ?? {});
      if (labelsByModel && changedModelIds.length > 0 && isCurrentImage()) {
        dispatch(
          annotatorActions.recordSave({
            step: {changedModelIds, payloadByModel: labelsByModel},
            savedAt: Date.now(),
          }),
        );
      }
      showNotification({
        severity: 'success',
        message: tolgeeInstance.t('classAssignedSuccessfully', {
          name: labelName,
          count: payload.length,
          ns: 'review',
        }),
      });
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
      if (isCurrentImage()) {
        dispatch(annotatorActions.setIsSaving(false));
      }
    }
  };
};

export const classifyImageWithDefaultMachineLabel = ({
  image,
  models,
  uiContext,
  telemetry,
}: {
  image: ImageData;
  models?: Model[];
  uiContext: string;
  telemetry?: AnnotationTelemetryPayload;
}) => {
  const labelsByModel = models?.reduce<ImageLabelPayloadByModel>((aggLabels, model) => {
    if (!model.defaultMachineLabel) return aggLabels;
    return {
      ...aggLabels,
      [model.id]: [{id: model.defaultMachineLabel, type: 'class', annotationEventType: 'manual'}],
    };
  }, {});
  return classifySingleImage({image, labelsByModel, labelName: CLASS_OK, uiContext, telemetry});
};

export const classifyImageWithClassLabel = ({
  image,
  modelId,
  labelItem,
  uiContext,
  telemetry,
}: {
  image: ImageData;
  modelId: string;
  labelItem: LabelItem;
  uiContext: string;
  telemetry?: AnnotationTelemetryPayload;
}) => {
  return classifySingleImage({
    image,
    labelsByModel: {[modelId]: [{id: labelItem.id, type: 'class', annotationEventType: 'manual'}]},
    labelName: labelItem.alias ?? labelItem.id,
    uiContext,
    telemetry,
  });
};

export const deleteImages = (itemIDs: string[] = []): ThunkAction<Promise<void>, ReduxState, unknown, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      dispatch(increaseBlockingLoadingIndicator());
      const projectID = selectCurrentProjectId(getState());

      await API.deleteImages(projectID, itemIDs);

      // delete images from cached images data
      dispatch(imageDataActions.deleteImages(itemIDs));
    } catch (error: any) {
      // Not an IMAGES_FETCH_ERROR: the gallery refetch that follows a delete would clear it before it is seen.
      Sentry.captureException(error);
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};

export const tagImages = ({imageIds, tag}: {imageIds: string[]; tag: string | string[]}) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      const state = getState();
      const projectID = selectCurrentProjectId(state);
      const tags = Array.isArray(tag) ? tag : [tag];
      await API.tagImages(projectID, {ids: imageIds, tags});
      dispatch(imageDataActions.addTag({imageIds, tag: tags}));
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    }
  };
};

export const untagImages = (imageIds: string[], tag: string | string[]) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      const state = getState();

      const projectID = selectCurrentProjectId(state);
      const tags = Array.isArray(tag) ? tag : [tag];

      await API.untagImages(projectID, {ids: imageIds, tags});
      dispatch(imageDataActions.removeTag({imageIds, tag: tags}));
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    }
  };
};

export const bulkTagImages = ({filters, tag}: {filters: ImageFilters; tag: string | string[]}) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      dispatch(increaseBlockingLoadingIndicator());
      const state = getState();
      const projectID = selectCurrentProjectId(state);
      const metadata = selectMetadata(state);
      const tags = Array.isArray(tag) ? tag : [tag];

      await API.bulkTagImages(projectID, {filters, tags}, metadata);
      dispatch(imageDataActions.resetImages());
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};

export const bulkUntagImages = ({filters, tag}: {filters: ImageFilters; tag: string | string[]}) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      dispatch(increaseBlockingLoadingIndicator());
      const state = getState();
      const projectID = selectCurrentProjectId(state);
      const metadata = selectMetadata(state);
      const tags = Array.isArray(tag) ? tag : [tag];

      await API.bulkUntagImages(projectID, {filters, tags}, metadata);
      dispatch(imageDataActions.resetImages());
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};

export const updateMeta = (imagesMeta: ImageMeta[]) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      dispatch(increaseBlockingLoadingIndicator());
      const state = getState();
      const projectID = selectCurrentProjectId(state);
      const isOnlyProductTypeChanges = imagesMeta.every((imageMeta) => imageMeta.meta?.productType);
      await API.updateMeta(projectID, imagesMeta);

      dispatch(imageDataActions.addMeta(imagesMeta));
      await dispatch(fetchFilters() as any);

      if (isOnlyProductTypeChanges) {
        showNotification({
          severity: 'success',
          message: tolgeeInstance.t('productTypeAssignedSuccessfully', {
            name: imagesMeta[0].meta?.productType,
            count: imagesMeta.length,
            ns: 'review',
          }),
        });
      }
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};

export const bulkUpdateMeta = ({filters, meta}: {filters: ImageFilters; meta: ImageMetadata}) => {
  return async (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>, getState: () => ReduxState): Promise<void> => {
    try {
      dispatch(increaseBlockingLoadingIndicator());
      const state = getState();
      const projectID = selectCurrentProjectId(state);
      const metadata = selectMetadata(state);
      await API.bulkUpdateMeta(projectID, {filters, meta}, metadata);
      await dispatch(fetchFilters() as any);
    } catch (error: any) {
      showNotification({severity: 'error', message: tolgeeInstance.t('genericError', {ns: 'error'})});
    } finally {
      dispatch(decreaseGlobalLoadingIndicator());
    }
  };
};
